import type { Cascadify, Maybe } from './util'
import type {
  ActivityStepMedia,
  CascadeActivityStepMedia,
  ActivityStepMediaId,
} from './activity-step-media'

export enum ActivityStepType {
  Speaker = 'speaker',
  Display = 'display',
  Play = 'play',
  Vote = 'vote',
  Result = 'result',
}

export enum ActivityStepLayout {
  Default = 'default',
  Social = 'social',
  Highlight = 'highlight',
  Meeting = 'meeting',
  Huddle = 'huddle',
  Theater = 'theater',
}

export enum ActivityStepAudio {
  UserCanHearEveryone = 'user-can-hear-everyone',
  UserCanHearHostOnly = 'user-can-hear-host-only',
  UserCanHearTemmates = 'user-can-hear-teammates',
  Auto = 'auto',
}

export type ActivityStepId = number
export type RootActivityStep = {
  id: ActivityStepId
  activityId: number
  position: number
  type: ActivityStepType
  layout: ActivityStepLayout
  duration: number
  audio: ActivityStepAudio
  notes?: Maybe<string>
  settings: { unlimitedTeams?: boolean }
}

type BaseActivityStep = RootActivityStep & { media: ActivityStepMedia[] }

export type ActivityStepSpeaker = BaseActivityStep & {
  type: ActivityStepType.Speaker
  settings: Record<string, never>
}

export type ActivityStepDisplay = BaseActivityStep & {
  type: ActivityStepType.Display
  settings: { text?: string; party?: boolean }
}

export type ActivityStepPlay<T extends Record<string, unknown>> =
  BaseActivityStep & {
    type: ActivityStepType.Play
    settings: T & {
      passScribe?: boolean
      disableActionSfx?: boolean
      teamCorrectToAdvance?: boolean
    }
  }

type Question = {
  amount?: number
  answer?: string
  category?: string
  difficulty?: number
  double?: boolean
  round?: number
  text?: string
}

export type Category = {
  value?: string
}

type Round = {
  categories: Record<string, Category>
  questions: Record<string, Question>
}

export enum Rounds {
  Round1 = 'round_1',
  Round2 = 'round_2',
  Final = 'final',
}

export type ActivityStepJeopardAIPlay = ActivityStepPlay<{
  predefined?: boolean
  rounds: Record<Rounds, Round>
}>

export type ActivityStepTextEntryPlay = ActivityStepPlay<{
  question: string
  answer: string
}>

export type ActivityStepTextFreeformPlay = ActivityStepPlay<{
  question: string
  password?: string
  highestNumber?: boolean
  noMedia?: boolean
}>

export type ActivityStepMagicNumberPlay = ActivityStepPlay<{
  high: string
  low: string
  number: number
}>

export type ActivityStepDiscussionGroupPlay = ActivityStepPlay<{
  labels: string[]
  instructions: string[]
}>

export type ActivityStepQuestionCardsPlay = ActivityStepPlay<{
  questions: string[]
}>

export enum TeamNameType {
  Name = 'name',
  Image = 'image',
  NameImage = 'name-image',
}

export type ActivityStepTeamNamePlay = ActivityStepPlay<{
  text: string
  type?: TeamNameType
}>

export type ActivityStepUnconditionalPositiveRegardPlay = BaseActivityStep

export type ActivityStepCategoriesPlay = ActivityStepPlay<{
  question: string
  answer: string[]
}>

export type ActivityStepCharadesPlay = ActivityStepPlay<{
  text: string
  answer: string[]
}>

export type ActivityStepFamilyFeudPlay = ActivityStepPlay<{
  question: string
  answer: string[]
  enableFastMoney?: boolean
  fastMoneyQuestions?: Array<{
    question: string
    answers: Array<{ text: string; number: number }>
  }>
}>

export type ActivityStepMultipleChoicePlay = ActivityStepPlay<{
  question: string
  answer: { id: string; text: string; correct: boolean }[]
  rightWrongIndicationOff?: boolean
  correctHidden?: boolean
}>

export type LipdubLyricNode = { text: string; offset?: number; id?: string }
export type ActivityStepLipdubPlay = ActivityStepPlay<{
  lyrics: LipdubLyricNode[]
}>

export type ActivityStepBuzzInPlay = ActivityStepPlay<{
  text: string
  zoomable: boolean
  delay?: number
}>

export type ActivityStepPhotoSequencesPlay = ActivityStepPlay<{
  text?: string
  minPhotos?: number
  maxPhotos?: number
  captioned?: boolean
}>

export type ActivityStepMusicalKeysPlay = ActivityStepPlay<{
  answers: string[]
}>

export type ActivityStepDefaultPlay = ActivityStepPlay<{
  text: string
}>

export type ActivityStepYouTubePlay = ActivityStepPlay<{
  party: boolean
}>

export type ActivityStepTeamPhotoPlay = ActivityStepPlay<{
  hybrid?: boolean
  captioned?: boolean
}>

export type ActivityStepPhotoBoothPlay = ActivityStepPlay<{
  hybrid?: boolean
  text: string
}>

export type Slide = {
  id: string
  mediaId: ActivityStepMediaId
  duration: number
}

export type ActivityStepSlidesPlay = ActivityStepPlay<{
  karaoke?: Maybe<boolean>
  perTeam?: Maybe<number>
  downloadable?: Maybe<boolean>
  slides: Slide[]
}>

export type ActivityStepGiphyPlay = ActivityStepPlay<{
  text: string
}>

export type ActivityStepQRCodePlay = ActivityStepPlay<{
  answer?: string
  text?: string
}>

export type ActivityStepWordCloudPlay = ActivityStepPlay<{
  text?: Maybe<string>
  max?: Maybe<number>
}>

export type ActivityStepRatingPollPlay = ActivityStepPlay<{
  text?: Maybe<string>
}>

export type ActivityStepFactMatchPlay = ActivityStepPlay<{ text?: string }>

export type ActivityStepPollPlay = ActivityStepPlay<{
  question: string
  answer: [{ id: string; text: string }]
  trw?: boolean
  host?: boolean
  nominateScribe?: boolean
  trwRandom?: boolean
  trwOptionsNumber?: number
}>

export type ActivityStepPriceIsRightPlay = ActivityStepPlay<{
  answer: string
  text?: string
  noCurrency?: boolean
}>

export type ActivityStepDrawingEachTeam = ActivityStepPlay<{
  text: string
  hybrid?: boolean
}>

export type ActivityStepTelephonePictionary = ActivityStepPlay<{
  text: string
}>

export type ActivityStepDrawingPictionaryPlay = ActivityStepPlay<{
  answer: string
}>

export type MatchPairNode =
  | { type: 'text'; text: string; id: string }
  | { type: 'media'; id: ActivityStepMediaId }

export type MatchPair = {
  id: string
  left: MatchPairNode
  right: MatchPairNode
}

export type RoyalRumblePair = {
  id: string
  top: MatchPairNode | null
  bottom: MatchPairNode | null
}

export enum ActivityMatchType {
  Image = 'image',
  Text = 'text',
  ImageText = 'image-text',
}

export type Gift = {
  id: string
  label?: Maybe<string>
  url?: Maybe<string>
}

export type ActivityStepMatchPlay = ActivityStepPlay<{
  type: ActivityMatchType
  pairs: MatchPair[]
  transparentBackgroundTop?: boolean
  transparentBackgroundBottom?: boolean
  matchImageWithText?: boolean
}>

export type ActivityStepOrderTheCardsPlay = ActivityStepPlay<{
  type: ActivityMatchType
  pairs: MatchPair[]
}>

export type ActivityStepRoyalRumblePlay = ActivityStepPlay<{
  type: ActivityMatchType
  pairs: RoyalRumblePair[]
}>

export type ActivityStepAwardsPlay = ActivityStepPlay<{
  contenders: boolean
  text?: string
}>

export type ActivityStepEvaluatePlay = ActivityStepPlay<Record<string, never>>

export type ActivityStepTakePhotoPlay = ActivityStepPlay<{
  text?: string
  social?: boolean
  captioned?: boolean
}>

export type ActivityStepVideoIndividualPlay = ActivityStepPlay<{
  text?: string
  captioned?: boolean
  /** in seconds */
  maxDuration?: number
}>

export type ActivityStepVideoTeamPlay = ActivityStepPlay<{
  captioned?: boolean
}>

export type ActivityStepWhiteElephantPlay = ActivityStepPlay<{
  gifts: Gift[]
}>

export type ActivityStepPlayerRating = ActivityStepPlay<{
  text?: string
  noRating?: boolean
  password?: string
}>

export type ActivityStepHandMovementPlay = BaseActivityStep

export type ActivityStepHeadToHeadPlay = ActivityStepPlay<{
  text?: string
}>

export enum ActivityStepVoter {
  Everyone = 'everyone',
  Team = 'team',
  Host = 'host',
}

export type ActivityStepVote = BaseActivityStep & {
  type: ActivityStepType.Vote
  settings: {
    voter: ActivityStepVoter
    text: string
    pointsDisabled?: boolean
    one: boolean
    poll: boolean
    theater: boolean
  }
}

export enum ActivityStepScoreStyle {
  Activity = 'activity',
}

export type ActivityStepResult = BaseActivityStep & {
  type: ActivityStepType.Result
  settings: {
    style?: ActivityStepScoreStyle
    text?: string
  }
}

type StepUnion =
  | ActivityStepSpeaker
  | ActivityStepDisplay
  | ActivityStepTextEntryPlay
  | ActivityStepTextFreeformPlay
  | ActivityStepTeamNamePlay
  | ActivityStepUnconditionalPositiveRegardPlay
  | ActivityStepCategoriesPlay
  | ActivityStepDiscussionGroupPlay
  | ActivityStepQuestionCardsPlay
  | ActivityStepCharadesPlay
  | ActivityStepFamilyFeudPlay
  | ActivityStepMultipleChoicePlay
  | ActivityStepQRCodePlay
  | ActivityStepJeopardAIPlay
  | ActivityStepLipdubPlay
  | ActivityStepBuzzInPlay
  | ActivityStepPhotoSequencesPlay
  | ActivityStepMusicalKeysPlay
  | ActivityStepYouTubePlay
  | ActivityStepTeamPhotoPlay
  | ActivityStepPhotoBoothPlay
  | ActivityStepGiphyPlay
  | ActivityStepWordCloudPlay
  | ActivityStepRatingPollPlay
  | ActivityStepFactMatchPlay
  | ActivityStepPollPlay
  | ActivityStepPriceIsRightPlay
  | ActivityStepDrawingEachTeam
  | ActivityStepTelephonePictionary
  | ActivityStepDrawingPictionaryPlay
  | ActivityStepMatchPlay
  | ActivityStepAwardsPlay
  | ActivityStepEvaluatePlay
  | ActivityStepWhiteElephantPlay
  | ActivityStepPlayerRating
  | ActivityStepOrderTheCardsPlay
  | ActivityStepRoyalRumblePlay
  | ActivityStepSlidesPlay
  | ActivityStepHandMovementPlay
  | ActivityStepHeadToHeadPlay
  | ActivityStepVote
  | ActivityStepResult

export type ActivityStep = StepUnion

export type CascadeActivityStep = Omit<Cascadify<ActivityStep>, 'media'> & {
  media: CascadeActivityStepMedia[]
}
